import SecureFilesPage from "@/components/files/SecureFilesPage";
import SecureFileReceivePage from "@/components/files/SecureFileReceivePage";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RegionProvider } from "@/context/RegionContext";
import { Header } from "@/components/Header";
import { PasswordGenerator } from "@/components/PasswordGenerator";
import { PricingAndCustomOffers } from "@/components/PricingAndCustomOffers";
import { AdminPage } from "@/components/AdminPage";
import { SecretRevealPage } from "@/components/SecretRevealPage";
import SmsDeliveryPage from "@/components/SmsDeliveryPage";
import { AccountAuth } from "@/components/AccountAuth";
import { AccountDashboard } from "@/components/AccountDashboard";
import PaymentReturnPage from "@/components/billing/PaymentReturnPage";
import { Landing } from "@/components/Landing";
import { SecureRequestCreate } from "@/components/SecureRequestCreate";
import { SecureRequestDeposit } from "@/components/SecureRequestDeposit";
import { SecureRequestResult } from "@/components/SecureRequestResult";
import { ContactPage, AboutPage } from "@/components/public/ContactPage";
import { DocumentationPage } from "@/components/public/DocumentationPage";
import { PasswordTesterPage } from "@/components/public/PasswordTesterPage";
import { SecurityPage } from "@/components/trust/SecurityPage";
import { LegalNoticePage } from "@/components/legal/LegalNoticePage";
import { PrivacyPage } from "@/components/legal/PrivacyPage";
import { CookiesPage } from "@/components/legal/CookiesPage";
import { TermsPage } from "@/components/legal/TermsPage";
import { CommercialTermsPage } from "@/components/legal/CommercialTermsPage";
import { DpaPage } from "@/components/legal/DpaPage";
import { SubprocessorsPage } from "@/components/legal/SubprocessorsPage";
import { SecurityDisclosurePage } from "@/components/legal/SecurityDisclosurePage";
import { InvitationPage } from "@/components/InvitationPage";
import { ConnectedAppShell } from "@/components/app/ConnectedAppShell";
import { ConnectedHome } from "@/components/app/ConnectedHome";
import { SendHub } from "@/components/app/SendHub";
import { WorkspaceSectionPage } from "@/components/app/WorkspaceSectionPage";
import { ProfilePage } from "@/components/app/ProfilePage";
import { BillingSectionPage } from "@/components/app/BillingSectionPage";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const FooterLink = ({ to, children }) => (
  <Link to={to} className="block text-slate-500 hover:text-emerald-600 transition-colors py-0.5">{children}</Link>
);

const Footer = () => {
  const { t } = useTranslation("public");
  return (
    <footer className="border-t border-slate-200/70 relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-5 gap-8 text-sm">
        <div>
          <div className="font-heading font-bold text-slate-900 mb-3">{t("footer.product")}</div>
          <FooterLink to="/generateur">{t("footer.links.createLink")}</FooterLink>
          <FooterLink to="/demander">{t("footer.links.requestInfo")}</FooterLink>
          <FooterLink to="/tester-mot-de-passe">{t("footer.links.testPassword")}</FooterLink>
          <FooterLink to="/offres">{t("footer.links.offers")}</FooterLink>
        </div>
        <div>
          <div className="font-heading font-bold text-slate-900 mb-3">{t("footer.help")}</div>
          <FooterLink to="/documentation">{t("footer.links.helpCenter")}</FooterLink>
          <FooterLink to="/securite">{t("footer.links.security")}</FooterLink>
          <FooterLink to="/contact">{t("footer.links.contact")}</FooterLink>
          <FooterLink to="/a-propos">{t("footer.links.about")}</FooterLink>
        </div>
        <div>
          <div className="font-heading font-bold text-slate-900 mb-3">{t("footer.privacy")}</div>
          <FooterLink to="/confidentialite">{t("footer.links.confidentiality")}</FooterLink>
          <FooterLink to="/cookies">{t("footer.links.cookies")}</FooterLink>
          <FooterLink to="/sous-traitants">{t("footer.links.subprocessors")}</FooterLink>
          <FooterLink to="/dpa">{t("footer.links.dpa")}</FooterLink>
        </div>
        <div>
          <div className="font-heading font-bold text-slate-900 mb-3">{t("footer.legal")}</div>
          <FooterLink to="/mentions-legales">{t("footer.links.legalNotice")}</FooterLink>
          <FooterLink to="/cgu">{t("footer.links.cgu")}</FooterLink>
          <FooterLink to="/conditions-commerciales">{t("footer.links.commercialTerms")}</FooterLink>
          <FooterLink to="/signalement-securite">{t("footer.links.vulnDisclosure")}</FooterLink>
        </div>
        <div className="text-slate-400 text-xs">
          <div className="font-heading font-bold text-slate-900 mb-3 text-sm">doli.nc</div>
          {t("footer.doliRegion")}
        </div>
      </div>
      <div className="border-t border-slate-200/70 py-4 text-center text-xs text-slate-500">{t("footer.bottom")}</div>
    </footer>
  );
};

const LoadingScreen = () => (
  <div className="app-bg min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
);

const PublicShell = ({ children }) => (
  <div className="app-bg grain min-h-screen relative flex flex-col">
    <Header />
    <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 lg:py-14 relative z-10 flex-1 overflow-x-hidden">{children}</main>
    <Footer />
  </div>
);

const AdaptiveShell = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user ? <ConnectedAppShell>{children}</ConnectedAppShell> : <PublicShell>{children}</PublicShell>;
};

const PrivateShell = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/connexion" replace />;
  return <ConnectedAppShell>{children}</ConnectedAppShell>;
};

const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/compte" replace />;
  return <PublicShell><Landing /></PublicShell>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RegionProvider>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/generateur" element={<AdaptiveShell><PasswordGenerator /></AdaptiveShell>} />
              <Route path="/documentation" element={<AdaptiveShell><DocumentationPage /></AdaptiveShell>} />
              <Route path="/tester-mot-de-passe" element={<AdaptiveShell><PasswordTesterPage /></AdaptiveShell>} />
              <Route path="/offres" element={<AdaptiveShell><PricingAndCustomOffers /></AdaptiveShell>} />
              <Route path="/admin" element={<PrivateShell><AdminPage /></PrivateShell>} />
              <Route path="/connexion" element={<PublicShell><AccountAuth /></PublicShell>} />

              {/* Application connectée */}
              <Route path="/compte" element={<PrivateShell><ConnectedHome /></PrivateShell>} />
              <Route path="/envoyer" element={<PrivateShell><SendHub /></PrivateShell>} />
              <Route path="/compte/profil" element={<PrivateShell><ProfilePage /></PrivateShell>} />

              {/* Mon Workspace : vue générale, équipe, invitations et adhésions. */}
              <Route path="/compte/workspace" element={<PrivateShell><WorkspaceSectionPage section="workspace" workspaceView="overview" /></PrivateShell>} />
              <Route path="/compte/workspace/apercu" element={<PrivateShell><WorkspaceSectionPage section="workspace" workspaceView="overview" /></PrivateShell>} />
              <Route path="/compte/workspace/equipe" element={<PrivateShell><WorkspaceSectionPage section="workspace" workspaceView="team" /></PrivateShell>} />
              <Route path="/compte/workspace/invitations" element={<PrivateShell><WorkspaceSectionPage section="workspace" workspaceView="invitations" /></PrivateShell>} />
              <Route path="/compte/workspace/adhesions" element={<PrivateShell><WorkspaceSectionPage section="workspace" workspaceView="join" /></PrivateShell>} />

              <Route path="/compte/annuaire" element={<PrivateShell><WorkspaceSectionPage section="directory" /></PrivateShell>} />
              <Route path="/compte/historique" element={<PrivateShell><WorkspaceSectionPage section="history" /></PrivateShell>} />

              {/* Facturation : petites pages, mêmes composants métier. */}
              <Route path="/compte/facturation" element={<PrivateShell><BillingSectionPage section="subscription" /></PrivateShell>} />
              <Route path="/compte/facturation/abonnement" element={<PrivateShell><BillingSectionPage section="subscription" /></PrivateShell>} />
              <Route path="/compte/facturation/informations" element={<PrivateShell><BillingSectionPage section="company" /></PrivateShell>} />
              <Route path="/compte/facturation/paiement" element={<PrivateShell><BillingSectionPage section="payments" /></PrivateShell>} />
              <Route path="/compte/facturation/factures" element={<PrivateShell><BillingSectionPage section="documents" /></PrivateShell>} />
              <Route path="/compte/facturation/acces" element={<PrivateShell><BillingSectionPage section="access" /></PrivateShell>} />

              {/* Administration : sécurité, marque, domaines et paramètres avancés. */}
              <Route path="/compte/administration" element={<PrivateShell><WorkspaceSectionPage section="administration" adminView="security" /></PrivateShell>} />
              <Route path="/compte/administration/securite" element={<PrivateShell><WorkspaceSectionPage section="administration" adminView="security" /></PrivateShell>} />
              <Route path="/compte/administration/personnalisation" element={<PrivateShell><WorkspaceSectionPage section="administration" adminView="branding" /></PrivateShell>} />
              <Route path="/compte/administration/domaines" element={<PrivateShell><WorkspaceSectionPage section="administration" adminView="domains" /></PrivateShell>} />
              <Route path="/compte/administration/avance" element={<PrivateShell><WorkspaceSectionPage section="administration" adminView="advanced" /></PrivateShell>} />

              <Route path="/compte/paiement/retour" element={<PrivateShell><PaymentReturnPage /></PrivateShell>} />
              <Route path="/fichiers" element={<PrivateShell><SecureFilesPage /></PrivateShell>} />
              <Route path="/mon-workspace" element={<Navigate to="/compte/workspace" replace />} />

              {/* Route de secours pendant la recette de la nouvelle navigation. */}
              <Route path="/compte/ancien" element={<PrivateShell><AccountDashboard /></PrivateShell>} />

              <Route path="/demander" element={<AdaptiveShell><SecureRequestCreate /></AdaptiveShell>} />
              <Route path="/contact" element={<AdaptiveShell><ContactPage /></AdaptiveShell>} />
              <Route path="/a-propos" element={<AdaptiveShell><AboutPage /></AdaptiveShell>} />
              <Route path="/securite" element={<AdaptiveShell><SecurityPage /></AdaptiveShell>} />
              <Route path="/confidentialite" element={<AdaptiveShell><PrivacyPage /></AdaptiveShell>} />
              <Route path="/cookies" element={<AdaptiveShell><CookiesPage /></AdaptiveShell>} />
              <Route path="/mentions-legales" element={<AdaptiveShell><LegalNoticePage /></AdaptiveShell>} />
              <Route path="/cgu" element={<AdaptiveShell><TermsPage /></AdaptiveShell>} />
              <Route path="/conditions-commerciales" element={<AdaptiveShell><CommercialTermsPage /></AdaptiveShell>} />
              <Route path="/dpa" element={<AdaptiveShell><DpaPage /></AdaptiveShell>} />
              <Route path="/sous-traitants" element={<AdaptiveShell><SubprocessorsPage /></AdaptiveShell>} />
              <Route path="/signalement-securite" element={<AdaptiveShell><SecurityDisclosurePage /></AdaptiveShell>} />
              <Route path="/legal" element={<Navigate to="/mentions-legales" replace />} />

              {/* Parcours destinataires : aucune navigation applicative autour des contenus sensibles. */}
              <Route path="/request/:id/result" element={<SecureRequestResult />} />
              <Route path="/request/:id" element={<SecureRequestDeposit />} />
              <Route path="/invitation/:token" element={<InvitationPage />} />
              <Route path="/secret/:secretId" element={<SecretRevealPage />} />
              <Route path="/s/:token" element={<SmsDeliveryPage />} />
              <Route path="/f/:token" element={<SecureFileReceivePage />} />
            </Routes>
          </RegionProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-center" theme="light" richColors />
    </div>
  );
}

export default App;
